import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { AlertCircle, X } from 'lucide-react';
import { Button } from '@headlessui/react';

interface ConfirmDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  isDeleting?: boolean;
  // Lets non-delete confirmations (e.g. "discard unsaved changes") reuse
  // this same modal instead of a native window.confirm/alert.
  confirmLabel?: string;
  confirmingLabel?: string;
  // 'danger' (default) is for genuinely destructive actions — delete,
  // reject, remove. 'primary' is for confirmations that are simply
  // important, not destructive (e.g. submitting a profile), so red never
  // implies the action itself is harmful.
  variant?: 'danger' | 'primary';
  // 'horizontal' (default) is the standard delete-style layout — icon and
  // text side by side, buttons right-aligned. 'centered' matches
  // UnsavedChangesModal's structure (icon above the title, everything
  // centered, full-width split buttons) for confirmations that should read
  // as a deliberate checkpoint rather than a quick inline warning.
  layout?: 'horizontal' | 'centered';
}

const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Delete Item?',
  message = 'Are you sure you want to delete this item? This action cannot be undone.',
  isDeleting = false,
  confirmLabel = 'Delete',
  confirmingLabel = 'Deleting...',
  variant = 'danger',
  layout = 'horizontal',
}) => {
  const isPrimary = variant === 'primary';
  const isCentered = layout === 'centered';
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[100]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={`w-full max-w-md transform overflow-hidden rounded-2xl bg-[#1e212b] border border-primary-subtle ${isCentered ? 'p-8 text-center' : 'p-6 text-left'} align-middle shadow-xl transition-all relative`}
              >
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 text-neutral-medium hover:text-neutral transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>

                <div className={isCentered ? '' : 'flex items-start gap-4'}>
                  <div
                    className={`p-3 rounded-full shrink-0 ${isPrimary ? 'bg-[#7C3AED]/10' : 'bg-red-500/10'} ${
                      isCentered ? 'mx-auto mb-5 w-12 h-12 flex items-center justify-center' : ''
                    }`}
                  >
                    <AlertCircle className={`w-6 h-6 ${isPrimary ? 'text-[#7C3AED]' : 'text-red-500'}`} />
                  </div>
                  <div className={isCentered ? '' : 'pt-1'}>
                    <Dialog.Title
                      as="h3"
                      className={`text-lg font-semibold leading-6 text-neutral ${isCentered ? 'mb-2' : ''}`}
                    >
                      {title}
                    </Dialog.Title>
                    <div className={`text-sm text-neutral-medium ${isCentered ? 'leading-relaxed' : 'mt-2'}`}>
                      {message}
                    </div>
                  </div>
                </div>

                <div className={`mt-8 flex gap-3 ${isCentered ? 'flex-col sm:flex-row' : 'justify-end'}`}>
                  <button
                    type="button"
                    className={`px-4 py-2 font-medium text-sm text-neutral-medium border border-primary-subtle hover:text-neutral hover:bg-primary-subtle rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isCentered ? 'flex-1' : ''}`}
                    onClick={onClose}
                    disabled={isDeleting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={`px-4 py-2 font-medium text-sm text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
                      isCentered ? 'flex-1' : ''
                    } ${
                      isPrimary
                        ? 'bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] hover:opacity-90'
                        : 'bg-red-500 hover:bg-red-600'
                    }`}
                    onClick={onConfirm}
                    disabled={isDeleting}
                  >
                    {isDeleting ? confirmingLabel : confirmLabel}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ConfirmDeleteModal;
